import PaymentTermsSelect from '@/components/PaymentTermsSelect';
import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { fmtCents, fmtDate } from "@/lib/format";
import { formatLeadTime } from "@/lib/leadTime";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { CreateQuoteCustomer } from "./Customers";
import { selectQuoteCustomer } from "@/lib/quoteCustomerFlow";
import { workingDateAfter } from "@/lib/quoteDates";
import { Plus, Loader2, FileText, X, Search, Eye, Archive, ArchiveRestore, Trash2, Pencil } from "lucide-react";

const inputClass = "mt-1 block w-full px-3 py-2 border border-slate-300 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[var(--sq-accent)]";

function isoAfter(days = 0) {
  const date = new Date();
  date.setDate(date.getDate() + Number(days || 0));
  return date.toISOString().slice(0, 10);
}

function customerContacts(customer) {
  const rows = (customer?.contacts || []).filter((row) => row.is_active !== false);
  if (rows.length) return rows;
  if (customer && (customer.contact_name || customer.email || customer.phone)) {
    return [{ id: "legacy-primary", name: customer.contact_name || "Primary contact", role: "", email: customer.email || "", phone: customer.phone || "", is_primary: true }];
  }
  return [];
}

function shippingLocations(customer) {
  const rows = (customer?.shipping_locations || []).filter((row) => row.is_active !== false);
  if (rows.length) return rows;
  if (customer?.delivery_address) {
    return [{ id: "legacy-main", label: "Main", formatted_address: customer.delivery_address, is_primary: true }];
  }
  return [];
}

function addressText(row) {
  if (!row) return "";
  if (row.formatted_address) return row.formatted_address;
  return [row.line1, row.line2, row.suburb, row.state, row.postcode, row.country].filter(Boolean).join(", ");
}

function initialForm(customer, options) {
  const contacts = customerContacts(customer);
  const locations = shippingLocations(customer);
  const contact = contacts.find((row) => row.is_primary) || contacts[0];
  const location = locations.find((row) => row.is_primary) || locations[0];
  const defaults = options?.defaults || {};
  return {
    customer_id: customer?.id || "", contact_id: contact?.id || "", shipping_location_id: location?.id || "",
    reference: "", rfq_reference: "", customer_reference: "",
    payment_terms: customer?.payment_terms || options?.general?.payment_terms || "",
    lead_time_text: customer?.lead_time_text || (customer?.lead_time_days != null ? `${customer.lead_time_days} working days` : defaults.lead_time_text || (defaults.lead_time_days != null ? `${defaults.lead_time_days} working days` : "10 working days")),
    lead_time_days: customer?.lead_time_text ? customer.lead_time_days ?? null : customer?.lead_time_days ?? defaults.lead_time_days ?? null,
    valid_until: isoAfter(customer?.validity_days ?? defaults.validity_days ?? 30),
    required_date: workingDateAfter(customer?.lead_time_days ?? defaults.lead_time_days ?? 0),
    certificate_required: Boolean(defaults.certificate_required),
    tier_override: customer?.tier || options?.approval?.default_tier || "T2",
    pricing_formula: "exact", notes: defaults.notes || "",
  };
}

const quoteViews = {
  all: { title: "All Quotes", description: "Every quote in the current organisation." },
  expired: { title: "Expired", description: "Quotes past their valid-until date." },
  expiring: { title: "Quotes Near Expiry", description: "Active quotes expiring in the next 14 days." },
  draft: { title: "Draft", description: "Quotes still being prepared or repriced." },
  approval: { title: "Pending Approval", description: "Priced quotes requiring administrator approval." },
  ready: { title: "Ready to Send Quotes", description: "Current priced quotes that are ready for customer issue." },
  customer: { title: "Quotes With Customer", description: "Issued quotes awaiting a customer decision." },
  ordered: { title: "Won / Ordered Quotes", description: "Accepted quotes that have become orders." },
  attention: { title: "Quotes Requiring Attention", description: "Quotes that need missing details completed or the price recalculated." },
};

function approvalPending(quote) {
  const snapshot = quote.price_snapshot || {};
  const approval = snapshot.commercial?.approval || {};
  return Boolean(approval.required) && quote.approval?.inputs_fingerprint !== snapshot.inputs_fingerprint;
}

function viewMatches(quote, view) {
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const validUntil = quote.valid_until ? new Date(`${quote.valid_until}T00:00:00`) : null;
  if (view === "expired") return Boolean(validUntil && validUntil < today);
  if (view === "expiring") {
    if (!validUntil || validUntil < today) return false;
    const cutoff = new Date(today); cutoff.setDate(cutoff.getDate() + 14);
    return validUntil <= cutoff;
  }
  if (view === "draft") return quote.status === "draft";
  if (view === "approval") return approvalPending(quote);
  if (view === "ready") return quote.status === "priced" && !approvalPending(quote);
  if (view === "customer") return ["sent", "with_customer"].includes(quote.status);
  if (view === "ordered") return quote.status === "ordered" || Boolean(quote.order_id);
  if (view === "attention") return Boolean(quote.pricing_state && !["current", "priced"].includes(quote.pricing_state));
  return true;
}

export default function QuotesPage({ view = "all", createOnMount = false }) {
  const { session } = useAuth();
  const readOnly = session?.user?.role === "viewer";
  const admin = session?.user?.role === "admin";
  const testPricing = session?.organisation?.is_synthetic !== false;
  const importedRehearsal = String(session?.organisation?.commercial_data_status || "").startsWith("rehearsal_imported");
  const [quotes, setQuotes] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [options, setOptions] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError,setLoadError] = useState("");
  const [creating, setCreating] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [actionId, setActionId] = useState(null);
  const [filters, setFilters] = useState({ quote: "", customer: "", reference: "", showArchived: false });
  const [form, setForm] = useState(() => initialForm(null, null));
  const [paymentTermsOverridden,setPaymentTermsOverridden]=useState(false);
  const [addingContact, setAddingContact] = useState(false);
  const [addingCustomer, setAddingCustomer] = useState(false);
  const [contactDraft, setContactDraft] = useState({ name: "", role: "", email: "", phone: "", is_primary: false, is_active: true });
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const requestedCustomerId = searchParams.get("customer");
  const effectiveView = searchParams.get("view") || view;
  const createOpened = useRef(false);

  async function loadData() {
      setLoading(true);setLoadError("");
      try {
        const [q, c, o] = await Promise.all([api.get("/quotes",{params:{summary:true}}), api.get("/customers"), api.get("/settings/quote-options")]);
        const requested = c.data.find((row) => row.id === requestedCustomerId) || c.data[0];
        setQuotes(q.data); setCustomers(c.data); setOptions(o.data); setForm(initialForm(requested, o.data));
      } catch (err) {
        const detail=err?.response?.data?.detail;
        const message=typeof detail==='string'?detail:"Could not load quoting data. Retry to load customers and quote settings.";
        setLoadError(message);toast.error(message);
      } finally { setLoading(false); }
  }
  useEffect(() => {loadData();}, []);

  useEffect(() => {
    if (!loading && !loadError && createOnMount && !createOpened.current) {
      createOpened.current = true;
      openCreate();
    }
  // Opening once is deliberate: closing the dialog must not immediately reopen it.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, loadError, createOnMount]);

  const selectedCustomer = useMemo(() => customers.find((row) => row.id === form.customer_id), [customers, form.customer_id]);
  const contacts = useMemo(() => customerContacts(selectedCustomer), [selectedCustomer]);
  const locations = useMemo(() => shippingLocations(selectedCustomer), [selectedCustomer]);
  const selectedContact = contacts.find((row) => row.id === form.contact_id);
  const selectedLocation = locations.find((row) => row.id === form.shipping_location_id);
  const selectedTier = options?.approval?.tiers?.[form.tier_override];
  const visibleQuotes = useMemo(() => quotes.filter((quote) => {
    if (!viewMatches(quote, effectiveView)) return false;
    if (!filters.showArchived && quote.archived) return false;
    const customer = customers.find((row) => row.id === quote.customer_id);
    const quoteNeedle = filters.quote.trim().toLowerCase();
    const customerNeedle = filters.customer.trim().toLowerCase();
    const referenceNeedle = filters.reference.trim().toLowerCase();
    if (quoteNeedle && !String(quote.reference || "").toLowerCase().includes(quoteNeedle)) return false;
    if (customerNeedle && !String(customer?.name || quote.customer_snapshot?.name || "").toLowerCase().includes(customerNeedle)) return false;
    const combinedReference = [quote.rfq_reference, quote.customer_reference].filter(Boolean).join(" ").toLowerCase();
    return !referenceNeedle || combinedReference.includes(referenceNeedle);
  }), [quotes, customers, filters, effectiveView]);
  const viewCopy = quoteViews[effectiveView] || quoteViews.all;

  function openCreate() {
    setForm(initialForm(customers.find((row) => row.id === requestedCustomerId) || customers[0], options));setPaymentTermsOverridden(false);
    setAddingContact(false);
    setShowModal(true);
  }

  function changeCustomer(customerId) {
    const customer = customers.find((row) => row.id === customerId);
    setForm((draft) => selectQuoteCustomer(draft, customer, initialForm(customer, options),paymentTermsOverridden));
    setAddingContact(false);
  }

  function customerCreated(customer) {
    setCustomers((rows) => [...rows.filter((row) => row.id !== customer.id), customer]);
    setForm((draft) => selectQuoteCustomer(draft, customer, initialForm(customer, options),paymentTermsOverridden));
    setAddingContact(false);
    setAddingCustomer(false);
  }

  async function addContact(e) {
    e.preventDefault();
    if (!selectedCustomer || !contactDraft.name.trim()) return;
    try {
      const { data } = await api.post(`/customers/${selectedCustomer.id}/contacts`, {
        expected_version: selectedCustomer.version || 0,
        contact: { ...contactDraft, name: contactDraft.name.trim(), role: contactDraft.role.trim(), email: contactDraft.email.trim(), phone: contactDraft.phone.trim() },
      });
      setCustomers((rows) => rows.map((row) => row.id === data.id ? data : row));
      const created = data.contacts[data.contacts.length - 1];
      setForm((old) => ({ ...old, contact_id: created.id }));
      setContactDraft({ name: "", role: "", email: "", phone: "", is_primary: false, is_active: true });
      setAddingContact(false);
      toast.success("Contact added");
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not add contact"); }
  }

  async function submit(e) {
    e.preventDefault();
    if (creating) return;
    if (!form.customer_id) { toast.error("Customer is required"); return; }
    if (options?.general?.customer_reference_required && !form.customer_reference.trim()) { toast.error("Customer reference is required"); return; }
    setCreating(true);
    try {
      const { data } = await api.post("/quotes", { ...form, contact_id: form.contact_id || null, shipping_location_id: form.shipping_location_id || null });
      setShowModal(false);
      nav(`/quotes/${data.id}`);
    } catch (err) { toast.error(err?.response?.data?.detail || "Create failed"); }
    finally { setCreating(false); }
  }

  async function openPreview(quote) {
    setPreviewLoading(true);
    try {
      const { data } = await api.get(`/quotes/${quote.id}`);
      setPreview(data);
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not load quote preview"); }
    finally { setPreviewLoading(false); }
  }

  async function setArchived(quote, archived) {
    setActionId(quote.id);
    try {
      const { data } = await api.post(`/quotes/${quote.id}/archive`, { archived });
      setQuotes((rows) => rows.map((row) => row.id === quote.id ? { ...row, ...data } : row));
      toast.success(archived ? `${quote.reference} archived` : `${quote.reference} restored`);
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not update quote"); }
    finally { setActionId(null); }
  }

  async function removeQuote(quote) {
    if (!window.confirm(`Delete unpriced draft ${quote.reference}? This cannot be undone.`)) return;
    setActionId(quote.id);
    try {
      await api.delete(`/quotes/${quote.id}`);
      setQuotes((rows) => rows.filter((row) => row.id !== quote.id));
      toast.success(`${quote.reference} deleted`);
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not delete quote"); }
    finally { setActionId(null); }
  }

  if (addingCustomer) return <CreateQuoteCustomer onCreated={customerCreated} onCancel={() => setAddingCustomer(false)} />;

  return (
    <div className="p-6 lg:p-8 max-w-[1500px] mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-display text-3xl font-bold">{viewCopy.title}</h1>
          <p className="text-sm text-slate-500 mt-1">{viewCopy.description}</p>
        </div>
        <button disabled={readOnly||loading||!!loadError} data-testid="create-quote-button" onClick={openCreate} className="inline-flex items-center gap-2 bg-[var(--sq-sidebar)] text-white text-sm font-medium px-4 py-2 rounded hover:bg-slate-800 disabled:opacity-50">
          <Plus className="w-4 h-4" /> New quote
        </button>
      </div>
      {loadError&&<div role="alert" className="mb-4 flex items-center justify-between gap-4 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800"><span>{loadError}</span><button type="button" className="rounded border border-red-300 bg-white px-3 py-1.5 font-semibold" disabled={loading} onClick={loadData}>Retry</button></div>}

      <section className="mb-4 rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
        <div className="grid gap-3 lg:grid-cols-[1fr_1fr_1fr_auto] lg:items-end">
          <Filter label="Quote number" value={filters.quote} onChange={(value) => setFilters((old) => ({ ...old, quote: value }))} placeholder="Search quote number" />
          <Filter label="Customer" value={filters.customer} onChange={(value) => setFilters((old) => ({ ...old, customer: value }))} placeholder="Search customer" />
          <Filter label="Reference" value={filters.reference} onChange={(value) => setFilters((old) => ({ ...old, reference: value }))} placeholder="RFQ or customer reference" />
          <label className="flex h-10 items-center gap-2 rounded-lg border border-slate-200 px-3 text-sm text-slate-700"><input type="checkbox" checked={filters.showArchived} onChange={(e) => setFilters((old) => ({ ...old, showArchived: e.target.checked }))} />Show archived quotes</label>
        </div>
        <div className="mt-3 flex items-center gap-2 text-xs text-slate-500"><Search className="h-3.5 w-3.5" />{visibleQuotes.length} quote{visibleQuotes.length === 1 ? "" : "s"} shown</div>
      </section>

      <div className="bg-white border border-slate-200 rounded-xl overflow-x-auto shadow-sm">
        <table data-testid="quote-list-table" className="w-full min-w-[1040px] text-sm">
          <thead className="bg-slate-50 border-b border-slate-200"><tr className="text-left"><Th>Quote number</Th><Th>Customer</Th><Th>Reference</Th><Th>Status</Th><Th>Expires</Th><Th className="text-right">{testPricing ? "Test total (AUD)" : "Total (AUD)"}</Th><Th>Updated</Th><Th /></tr></thead>
          <tbody>
            {loading && <tr><td colSpan={8} className="p-6 text-center text-slate-500 font-mono text-xs"><Loader2 className="w-4 h-4 inline animate-spin mr-2" />loading…</td></tr>}
            {!loading && !loadError && visibleQuotes.length === 0 && <tr><td colSpan={8} data-testid="quote-list-empty" className="p-8 text-center text-slate-500">No quotes match this view and filters.</td></tr>}
            {visibleQuotes.map((q) => {
              const cust = customers.find((c) => c.id === q.customer_id);
              return <tr key={q.id} data-testid={`quote-row-${q.id}`} className={`border-b border-slate-100 hover:bg-slate-50 ${q.archived ? "opacity-60" : ""}`}>
                <td className="px-4 py-3 numeric font-medium"><Link to={`/quotes/${q.id}`} className="hover:underline">{q.reference}</Link></td>
                <td className="px-4 py-3">{cust?.name || q.customer_snapshot?.name || <span className="text-slate-400">—</span>}</td>
                <td className="px-4 py-3 text-xs text-slate-600"><div>{q.rfq_reference || q.customer_reference || "—"}</div>{q.rfq_reference && q.customer_reference && <div className="mt-0.5 text-slate-400">{q.customer_reference}</div>}</td>
                <td className="px-4 py-3"><span className={`text-[11px] px-2 py-0.5 rounded-full border ${approvalPending(q) ? "bg-amber-50 text-amber-800 border-amber-200" : q.status === "priced" ? "bg-emerald-50 text-emerald-800 border-emerald-200" : q.status === "sent" ? "bg-blue-50 text-blue-800 border-blue-200" : "bg-slate-100 text-slate-600 border-slate-200"}`}>{approvalPending(q) ? "pending approval" : testPricing && q.status === "priced" ? "test priced" : q.status}</span></td>
                <td className="px-4 py-3 text-xs text-slate-500">{dateOnly(q.valid_until)}</td>
                <td className="px-4 py-3 numeric text-right">{fmtCents(q.total_cents)}</td>
                <td className="px-4 py-3 text-slate-500 text-xs numeric">{fmtDate(q.updated_at)}</td>
                <td className="px-4 py-3"><div className="flex justify-end gap-1">
                  <button title="Preview quote" disabled={previewLoading} onClick={() => openPreview(q)} className="rounded-lg border p-2 text-slate-600 hover:bg-slate-100 disabled:opacity-40"><Eye className="h-4 w-4" /></button>
                  <Link title="Open quote" data-testid={`quote-open-${q.id}`} to={`/quotes/${q.id}`} className="rounded-lg border p-2 text-slate-700 hover:bg-slate-100"><Pencil className="h-4 w-4" /></Link>
                  <button title={q.archived ? "Restore quote" : "Archive quote"} disabled={readOnly || actionId === q.id} onClick={() => setArchived(q, !q.archived)} className="rounded-lg border p-2 text-slate-600 hover:bg-slate-100 disabled:opacity-40">{q.archived ? <ArchiveRestore className="h-4 w-4" /> : <Archive className="h-4 w-4" />}</button>
                  {admin && q.status === "draft" && q.total_cents == null && <button title="Delete unpriced draft" disabled={actionId === q.id} onClick={() => removeQuote(q)} className="rounded-lg border p-2 text-rose-600 hover:bg-rose-50 disabled:opacity-40"><Trash2 className="h-4 w-4" /></button>}
                </div></td>
              </tr>;
            })}
          </tbody>
        </table>
      </div>

      {preview && <QuotePreview quote={preview} customer={customers.find((row) => row.id === preview.customer_id)} onClose={() => setPreview(null)} onOpen={() => nav(`/quotes/${preview.id}`)} />}

      {showModal && <div className="fixed inset-0 bg-slate-900/50 z-50 overflow-y-auto p-4 lg:p-8" onMouseDown={() => !creating && setShowModal(false)}>
        <form onMouseDown={(e) => e.stopPropagation()} onSubmit={submit} className="bg-white border border-slate-200 rounded-xl shadow-2xl w-full max-w-5xl mx-auto">
          <div className="flex items-start justify-between border-b border-slate-200 px-6 py-5">
            <div><h2 className="font-display text-2xl font-semibold">New quote</h2></div>
            <button type="button" aria-label="Close" onClick={() => setShowModal(false)} className="p-2 rounded hover:bg-slate-100"><X className="w-5 h-5" /></button>
          </div>

          <div className="p-6 grid md:grid-cols-2 lg:grid-cols-3 gap-x-5 gap-y-4">
            <Field label="Customer" className="lg:col-span-2"><div className="flex gap-2">
              <select data-testid="create-quote-customer-select" value={form.customer_id} onChange={(e) => changeCustomer(e.target.value)} className={inputClass} required>
                <option value="">Select customer</option>
                {customers.map((customer) => <option key={customer.id} value={customer.id}>{customer.name}{customer.account_code ? ` · ${customer.account_code}` : ""}{customer.tier ? ` · ${customer.tier}` : ""}</option>)}
              </select>
              {admin && <button type="button" disabled={creating} onClick={() => setAddingCustomer(true)} className="mt-1 whitespace-nowrap px-3 py-2 border rounded-md text-sm hover:bg-slate-50">Add customer</button>}
            </div></Field>

            <Field label="Quote number"><input data-testid="create-quote-reference-input" value={form.reference} onChange={(e) => setForm((old) => ({ ...old, reference: e.target.value }))} className={`${inputClass} font-mono`} placeholder="Automatic" /></Field>

            <Field label="Contact" className="lg:col-span-2"><div className="flex gap-2">
              <select value={form.contact_id} onChange={(e) => setForm((old) => ({ ...old, contact_id: e.target.value }))} className={inputClass}><option value="">No contact selected</option>{contacts.map((contact) => <option key={contact.id} value={contact.id}>{contact.name}{contact.role ? ` · ${contact.role}` : ""}</option>)}</select>
              <button type="button" disabled={!selectedCustomer} onClick={() => setAddingContact((value) => !value)} className="mt-1 whitespace-nowrap px-3 py-2 border rounded-md text-sm hover:bg-slate-50 disabled:opacity-50">Add contact</button>
            </div></Field>
            <Field label="Contact email"><input readOnly value={selectedContact?.email || ""} className={`${inputClass} bg-slate-50 text-slate-600`} placeholder="No email recorded" /></Field>

            {addingContact && <div className="lg:col-span-3 rounded-lg border border-slate-200 bg-slate-50 p-4">
              <div className="font-medium text-sm mb-3">Add contact to {selectedCustomer?.name}</div>
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-3">
                {[["name", "Name", true], ["role", "Role"], ["email", "Email"], ["phone", "Phone"]].map(([key, label, required]) => <Field key={key} label={label}><input required={Boolean(required)} type={key === "email" ? "email" : "text"} value={contactDraft[key]} onChange={(e) => setContactDraft((old) => ({ ...old, [key]: e.target.value }))} className={inputClass} /></Field>)}
              </div>
              <label className="inline-flex items-center gap-2 mt-3 text-sm"><input type="checkbox" checked={contactDraft.is_primary} onChange={(e) => setContactDraft((old) => ({ ...old, is_primary: e.target.checked }))} />Make primary contact</label>
              <div className="flex gap-2 mt-3"><button type="button" onClick={addContact} className="px-3 py-2 rounded-md bg-slate-900 text-white text-sm">Save contact</button><button type="button" onClick={() => setAddingContact(false)} className="px-3 py-2 rounded-md border text-sm">Cancel</button></div>
            </div>}

            <Field label="RFQ reference"><input value={form.rfq_reference} onChange={(e) => setForm((old) => ({ ...old, rfq_reference: e.target.value }))} className={inputClass} /></Field>
            <Field label={`Customer reference${options?.general?.customer_reference_required ? " *" : ""}`}><input required={Boolean(options?.general?.customer_reference_required)} value={form.customer_reference} onChange={(e) => setForm((old) => ({ ...old, customer_reference: e.target.value }))} className={inputClass} /></Field>
            <Field label="Pricing formula"><select value={form.pricing_formula} disabled={creating} onChange={e=>setForm(old=>({...old,pricing_formula:e.target.value}))} className={inputClass}><option value="exact">Exact quantity</option><option value="multi">Multi quantity</option></select><p className="text-[11px] text-slate-500 mt-1">{form.pricing_formula==='multi'?'Show separate prices for the other quantities entered on each part.':'Price the quantity entered on each part.'}</p></Field>

            <Field label="Required date"><input type="date" value={form.required_date} onChange={(e) => setForm((old) => ({ ...old, required_date: e.target.value }))} className={inputClass} /></Field>
            <Field label="Lead time"><input value={form.lead_time_text} maxLength={200} onChange={(e) => setForm((old) => ({ ...old, lead_time_text: e.target.value, lead_time_days: /^\d+$/.test(e.target.value) ? Number(e.target.value) : null }))} className={inputClass} /></Field>
            <Field label="Valid until"><input type="date" value={form.valid_until} onChange={(e) => setForm((old) => ({ ...old, valid_until: e.target.value }))} className={inputClass} /></Field>
            <Field label="Payment terms"><PaymentTermsSelect value={form.payment_terms} terms={options?.general?.standard_payment_terms||[]} onChange={value=>{setPaymentTermsOverridden(true);setForm(old=>({...old,payment_terms:value}));}} className={inputClass} emptyLabel="No payment terms" /></Field>

            <Field label="Customer tier"><select value={form.tier_override} onChange={(e) => setForm((old) => ({ ...old, tier_override: e.target.value }))} className={inputClass}>{Object.keys(options?.approval?.tiers || { T1: {}, T2: {}, T3: {} }).map((tier) => <option key={tier} value={tier}>{tier}</option>)}</select></Field>
            <Field label="Tier rules"><div className="mt-1 min-h-[42px] rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-600">{selectedTier ? `${selectedTier.markup_pct}% markup · max ${selectedTier.max_discount_pct}% discount` : "Uses the configured tier policy"}</div></Field>

            <Field label="Ship to" className="lg:col-span-2"><select value={form.shipping_location_id} onChange={(e) => setForm((old) => ({ ...old, shipping_location_id: e.target.value }))} className={inputClass}><option value="">No delivery location selected</option>{locations.map((location) => <option key={location.id} value={location.id}>{location.label}</option>)}</select>{selectedLocation && <p className="text-xs text-slate-500 mt-1">{addressText(selectedLocation)}</p>}</Field>
            <Field label="Test certificate"><label className="mt-1 min-h-[42px] rounded-md border border-slate-300 px-3 py-2 flex items-center gap-2 text-sm"><input type="checkbox" checked={form.certificate_required} onChange={(e) => setForm((old) => ({ ...old, certificate_required: e.target.checked }))} />Required</label></Field>
            <Field label="Notes" className="lg:col-span-3"><textarea rows={4} value={form.notes} onChange={(e) => setForm((old) => ({ ...old, notes: e.target.value }))} className={inputClass} /></Field>
          </div>

          <div className="flex items-center justify-between gap-4 border-t border-slate-200 px-6 py-4 bg-slate-50 rounded-b-xl">
            
            <div className="flex gap-2"><button type="button" onClick={() => setShowModal(false)} disabled={creating} className="px-4 py-2 text-sm text-slate-700 rounded-md border border-slate-300 bg-white hover:bg-slate-50">Cancel</button><button data-testid="create-quote-submit-button" type="submit" disabled={creating || !form.customer_id} className="inline-flex items-center gap-2 px-4 py-2 text-sm rounded-md bg-[var(--sq-sidebar)] text-white hover:bg-slate-800 disabled:opacity-60">{creating && <Loader2 className="w-4 h-4 animate-spin" />}{creating ? "Creating quote…" : "Create quote"}</button></div>
          </div>
        </form>
      </div>}
    </div>
  );
}

function Field({ label, className = "", children }) {
  return <label className={`block ${className}`}><span className="field-label">{label}</span>{children}</label>;
}

function Filter({ label, value, onChange, placeholder }) {
  return <label className="block"><span className="field-label">{label}</span><div className="relative mt-1"><Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" /><input value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="block h-10 w-full rounded-lg border border-slate-300 bg-white pl-9 pr-3 text-sm outline-none focus:ring-2 focus:ring-[var(--sq-accent)]" /></div></label>;
}

function dateOnly(value) {
  if (!value) return "—";
  const text = String(value).slice(0, 10);
  const [year, month, day] = text.split("-");
  return year && month && day ? `${day}/${month}/${year}` : text;
}

function quotePartCount(quote) {
  if (Array.isArray(quote.lines)) return quote.lines.length;
  if (Array.isArray(quote.price_snapshot?.priced_lines)) return quote.price_snapshot.priced_lines.length;
  return quote.part_count || 0;
}

function QuotePreview({ quote, customer, onClose, onOpen }) {
  const snapshot = quote.customer_snapshot || {};
  const rows = [
    ["Customer", customer?.name || snapshot.name || "—"],
    ["Contact", quote.contact_name || snapshot.contact_name || "—"],
    ["RFQ reference", quote.rfq_reference || "—"],
    ["Customer reference", quote.customer_reference || "—"],
    ["Required date", dateOnly(quote.required_date)],
    ["Created", fmtDate(quote.created_at)],
    ["Created by", quote.created_by_email || "—"],
    ["Quote number", quote.reference || "—"],
    ["Status", quote.archived ? `${quote.status} · archived` : quote.status || "—"],
    ["Customer tier", quote.tier_override || snapshot.tier || "—"],
    ["Payment terms", quote.payment_terms || "—"],
    ["Ship to", quote.ship_to || quote.shipping_address || "—"],
    ["Lead time", formatLeadTime(quote)],
    ["Expires", dateOnly(quote.valid_until)],
    ["Part count", quotePartCount(quote)],
    ["Total ex GST", fmtCents(quote.total_cents)],
  ];
  return <div className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-950/55 p-4" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
    <section role="dialog" aria-modal="true" aria-label={`Preview ${quote.reference}`} className="max-h-[92vh] w-full max-w-4xl overflow-y-auto rounded-2xl bg-white shadow-2xl">
      <header className="sticky top-0 z-10 flex items-start justify-between border-b bg-white px-6 py-5"><div><div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-400">Quote preview</div><h2 className="mt-1 flex items-center gap-2 text-2xl font-bold"><FileText className="h-5 w-5 text-red-600" />{quote.reference}</h2></div><button aria-label="Close preview" onClick={onClose} className="rounded-lg border p-2 hover:bg-slate-50"><X className="h-5 w-5" /></button></header>
      <div className="grid gap-x-8 gap-y-0 p-6 sm:grid-cols-2">{rows.map(([label, value]) => <div key={label} className="grid grid-cols-[140px_1fr] gap-3 border-b border-slate-100 py-3 text-sm"><span className="text-slate-500">{label}</span><b className="break-words font-medium text-slate-900">{value}</b></div>)}</div>
      <div className="px-6 pb-6"><div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Notes</div><div className="mt-2 min-h-20 whitespace-pre-wrap rounded-xl border bg-slate-50 p-4 text-sm text-slate-700">{quote.notes || "No quote notes."}</div></div>
      <footer className="sticky bottom-0 flex justify-end gap-2 border-t bg-slate-50 px-6 py-4"><button onClick={onClose} className="sq-secondary-button">Close</button><button onClick={onOpen} className="sq-primary-button"><Pencil className="h-4 w-4" />Edit Quote</button></footer>
    </section>
  </div>;
}

function Th({ children, className = "" }) {
  return <th className={`px-4 py-2.5 text-[11px] text-slate-500 font-medium ${className}`}>{children}</th>;
}
